import React from 'react'
import { useTheme } from '../../hooks/useTheme'

export type ModalButtonVariant = 'primary' | 'secondary' | 'danger'

interface ModalButtonProps {
  variant?: ModalButtonVariant
  onClick: () => void | Promise<void>
  children: React.ReactNode
  disabled?: boolean
  loading?: boolean
  className?: string
  type?: 'button' | 'submit'
}

const ModalButton: React.FC<ModalButtonProps> = ({
  variant = 'primary',
  onClick,
  children,
  disabled = false,
  loading = false,
  className = '',
  type = 'button',
}) => {
  const { colors } = useTheme()

  const getStyles = () => {
    switch (variant) {
      case 'primary':
        return {
          backgroundColor: colors.primary,
          color: '#FFFFFF',
          borderColor: colors.primary,
          hoverBackgroundColor: colors.primaryDark,
          hoverBorderColor: colors.primaryDark,
        }
      case 'danger':
        return {
          backgroundColor: colors.error,
          color: '#FFFFFF',
          borderColor: colors.error,
          hoverBackgroundColor: '#b91c1c',
          hoverBorderColor: '#b91c1c',
        }
      case 'secondary':
      default:
        return {
          backgroundColor: '#f3f4f6',
          color: colors.primary,
          borderColor: '#d1d5db',
          hoverBackgroundColor: '#e5e7eb',
          hoverBorderColor: '#d1d5db',
        }
    }
  }

  const styles = getStyles()
  const isDisabled = disabled || loading

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={isDisabled}
      className={`px-6 py-3 text-base rounded-lg font-semibold transition-all duration-300 ease-out cursor-pointer ${
        isDisabled ? 'opacity-50 cursor-not-allowed' : ''
      } ${className}`}
      style={{
        backgroundColor: styles.backgroundColor,
        color: styles.color,
        border: `2px solid ${styles.borderColor}`,
      }}
      onMouseEnter={(e) => {
        if (!isDisabled) {
          e.currentTarget.style.backgroundColor = styles.hoverBackgroundColor
          e.currentTarget.style.borderColor = styles.hoverBorderColor
        }
      }}
      onMouseLeave={(e) => {
        if (!isDisabled) {
          e.currentTarget.style.backgroundColor = styles.backgroundColor
          e.currentTarget.style.borderColor = styles.borderColor
        }
      }}
    >
      {loading ? (
        <span className="inline-flex items-center space-x-2">
          <svg
            className="animate-spin h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <span>{children}</span>
        </span>
      ) : (
        children
      )}
    </button>
  )
}

export default ModalButton
